import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import { AppCard, AppPageHeader } from '../../components';
import { CompanyPeriodsTable } from '../../components/Admin';
import { useCompaniesQuery } from '../../api/reference';
import { ICompany } from '../../interfaces';

const filterOptions = (options: ICompany[], { inputValue }: { inputValue: string }) => {
	const input = inputValue.toLowerCase();
	return options
		.filter((o) => o.symbol.toLowerCase().startsWith(input) || (o.name ?? '').toLowerCase().startsWith(input))
		.sort((a, b) => {
			const aMatch = a.symbol.toLowerCase().startsWith(input);
			const bMatch = b.symbol.toLowerCase().startsWith(input);
			return aMatch === bMatch ? 0 : aMatch ? -1 : 1;
		})
		.slice(0, 100);
};

export default function PeriodsPage() {
	const { data: companies, isLoading: companiesLoading } = useCompaniesQuery(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [inputValue, setInputValue] = React.useState('');
	const [open, setOpen] = React.useState(false);

	const companyIdParam = searchParams.get('companyId');
	const selected = React.useMemo(
		() => (companyIdParam && companies ? (companies.find((c) => c.id === Number(companyIdParam)) ?? null) : null),
		[companyIdParam, companies],
	);

	const handleChange = (_: React.SyntheticEvent, value: ICompany | null) => {
		if (value) {
			setSearchParams({ companyId: String(value.id) }, { replace: true });
		} else {
			setSearchParams({}, { replace: true });
		}
		setOpen(false);
	};

	return (
		<>
			<AppPageHeader>
				<Typography variant="h6">Periods</Typography>
				<Typography variant="body2" color="text.secondary">
					View period table entries for a company
				</Typography>
			</AppPageHeader>
			<AppCard>
				<Box sx={{ mb: 3, maxWidth: 480 }}>
					<Autocomplete
						options={companies ?? []}
						loading={companiesLoading}
						filterOptions={filterOptions}
						getOptionLabel={(option) => option.symbol}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						value={selected}
						onChange={handleChange}
						inputValue={inputValue}
						onInputChange={(_, value) => { setInputValue(value); setOpen(value.length >= 1); }}
						open={open}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="  Search by symbol or name…"
								variant="outlined"
								size="small"
							/>
						)}
						renderOption={(optProps, option) => (
							<li {...optProps} key={option.id}>
								<Stack spacing={2} alignItems="center" direction="row">
									<Typography>{option.symbol}</Typography>
									<Typography variant="body2" color="text.disabled">{option.name}</Typography>
								</Stack>
							</li>
						)}
						fullWidth
					/>
				</Box>
				{selected ? (
					<CompanyPeriodsTable companyId={selected.id} />
				) : (
					<Typography variant="body2" color="text.disabled">
						Select a company to view its periods.
					</Typography>
				)}
			</AppCard>
		</>
	);
}
